import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { translations } from '../constants/translations';
import { hooks } from '../hooks';
import axiosInstance from '../services/axiosInstance';

type Translations = typeof translations.en;

export interface LanguageInfo {
  code: string;
  name: string;
  is_built_in: boolean;
}

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: Translations;
  availableLanguages: LanguageInfo[];
  loadingLanguages: boolean;
}

const CACHE_PREFIX = 'langData_';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 jam

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function deepMerge(base: any, override: any): any {
  const result: any = {};
  for (const key of Object.keys(base)) {
    if (override?.[key] !== undefined && typeof override[key] === 'object' && !Array.isArray(override[key])) {
      result[key] = deepMerge(base[key], override[key]);
    } else if (override?.[key] !== undefined) {
      result[key] = override[key];
    } else {
      result[key] = base[key];
    }
  }
  return result;
}

function getBuiltIn(code: string): Translations | null {
  if (code === 'en') return translations.en;
  if (code === 'id') return translations.id;
  return null;
}

function loadCached(code: string): Translations | null {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${code}`);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) { localStorage.removeItem(`${CACHE_PREFIX}${code}`); return null; }
    return data;
  } catch { return null; }
}

function saveCache(code: string, data: Translations) {
  try { localStorage.setItem(`${CACHE_PREFIX}${code}`, JSON.stringify({ data, ts: Date.now() })); } catch { /* noop */ }
}

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<string>('en');
  const [t, setT] = useState<Translations>(translations.en);
  const [availableLanguages, setAvailableLanguages] = useState<LanguageInfo[]>([
    { code: 'en', name: 'English', is_built_in: true },
    { code: 'id', name: 'Indonesian', is_built_in: true },
  ]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const authSlice = hooks.useAppSelector((state) => state.authSlice);

  const applyLanguage = useCallback(async (code: string) => {
    const builtin = getBuiltIn(code);
    if (builtin) { setT(builtin); return; }

    const cached = loadCached(code);
    if (cached) { setT(cached); return; }

    try {
      const res = await axiosInstance.get(`/languages/${code}`);
      if (res.data.is_built_in) {
        const bt = getBuiltIn(code);
        if (bt) setT(bt);
        return;
      }
      const merged = deepMerge(translations.en, res.data.translations || {});
      setT(merged as Translations);
      saveCache(code, merged as Translations);
    } catch {
      setT(translations.en);
    }
  }, []);

  // Restore bahasa dari localStorage saat startup
  useEffect(() => {
    try {
      const saved = localStorage.getItem('appLanguage') || 'en';
      setLanguageState(saved);
      applyLanguage(saved);
    } catch {
      applyLanguage('en');
    }
  }, [applyLanguage]);

  // Fetch daftar bahasa tersedia + sync bahasa user dari DB saat login
  useEffect(() => {
    if (!authSlice.isAuthenticated || !authSlice.userId) return;

    const init = async () => {
      setLoadingLanguages(true);
      try {
        const [langListRes, userRes] = await Promise.all([
          axiosInstance.get('/languages'),
          axiosInstance.get(`/users/${authSlice.userId}`),
        ]);

        if (langListRes.data?.length > 0) {
          setAvailableLanguages(langListRes.data);
        }

        const userLang = userRes.data?.data?.app_language || userRes.data?.[0]?.app_language;
        if (userLang && userLang !== language) {
          setLanguageState(userLang);
          await applyLanguage(userLang);
          try { localStorage.setItem('appLanguage', userLang); } catch { /* noop */ }
        }
      } catch { /* noop */ } finally {
        setLoadingLanguages(false);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSlice.isAuthenticated, authSlice.userId]);

  const setLanguage = async (code: string) => {
    setLanguageState(code);
    await applyLanguage(code);
    try { localStorage.setItem('appLanguage', code); } catch { /* noop */ }

    if (authSlice.isAuthenticated && authSlice.userId) {
      try {
        await axiosInstance.post(`/users/${authSlice.userId}/updateUser`, { app_language: code });
      } catch { /* noop */ }
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, availableLanguages, loadingLanguages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};
